import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, UserPlus, Trash2, Copy, Check, Lock, Users, MailPlus, Wrench, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_STYLES = {
  admin: "bg-primary text-primary-foreground",
  curator: "bg-accent text-accent-foreground",
  learner: "bg-secondary text-secondary-foreground",
};

const RoleBadge = ({ role }) => (
  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold uppercase tracking-wide ${ROLE_STYLES[role] || ROLE_STYLES.learner}`}>
    {role}
  </span>
);

const AdminView = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("curator");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [migPlan, setMigPlan] = useState(null); // { found, changes, applied }
  const [migBusy, setMigBusy] = useState(false);
  const [migConfirm, setMigConfirm] = useState(false);

  const load = useCallback(async () => {
    const [u, i] = await Promise.all([api.get("/admin/users"), api.get("/admin/invites")]);
    setUsers(u.data);
    setInvites(i.data);
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [load, user]);

  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center relative z-10">
        <Lock className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-serif text-3xl text-foreground">Admin access only</h1>
        <p className="text-muted-foreground mt-2">Your current role is <b>{user.role}</b>.</p>
      </div>
    );
  }

  const changeRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.user_id}/role`, { role });
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, role } : x));
      toast.success(`${u.name} is now ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const createInvite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter an email"); return; }
    setCreating(true);
    try {
      const res = await api.post("/admin/invites", { email: inviteEmail.trim(), role: inviteRole });
      toast.success(res.data.accepted ? "Existing user updated" : "Invite created");
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/admin/invites/${id}`);
      setInvites((prev) => prev.filter((i) => i.id !== id));
      toast.success("Invite revoked");
    } catch { toast.error("Could not revoke"); }
  };

  const previewMigration = async () => {
    setMigBusy(true);
    try {
      const res = await api.post("/admin/migrate-slug-ids?apply=false");
      setMigPlan(res.data);
      toast.message(res.data.found ? `${res.data.found} legacy ID(s) to convert` : "All entity IDs are already slugs");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not check IDs"); }
    finally { setMigBusy(false); }
  };

  const applyMigration = async () => {
    setMigBusy(true);
    setMigConfirm(false);
    try {
      const res = await api.post("/admin/migrate-slug-ids?apply=true");
      setMigPlan(res.data);
      toast.success(`Migrated ${res.data.found} entity ID(s) to slugs`);
    } catch (e) { toast.error(e.response?.data?.detail || "Migration failed"); }
    finally { setMigBusy(false); }
  };

  const inviteLink = (inv) => `${window.location.origin}/?invite=${inv.token}`;
  const copyLink = (inv) => {
    navigator.clipboard.writeText(inviteLink(inv));
    setCopied(inv.id);
    setTimeout(() => setCopied(null), 1800);
    toast.success("Invite link copied");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
      <div className="flex items-center gap-2 text-primary text-sm font-semibold uppercase tracking-wide">
        <Shield className="w-4 h-4" /> Admin console
      </div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight-serif text-foreground mt-1">Manage People & Access</h1>
      <p className="text-muted-foreground mt-1">Update roles and invite new members. Admins have full access.</p>

      {/* Invite */}
      <div className="bg-card border border-border rounded-lg shadow-sm p-6 mt-8" data-testid="invite-panel">
        <div className="flex items-center gap-2 mb-4">
          <MailPlus className="w-5 h-5 text-primary" />
          <h2 className="font-serif text-2xl text-foreground">Invite a member</h2>
        </div>
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <Label>Email</Label>
            <Input
              type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="person@example.com" className="mt-1" data-testid="invite-email-input"
            />
          </div>
          <div className="w-full md:w-48">
            <Label>Role</Label>
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger className="mt-1" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="learner">Learner</SelectItem>
                <SelectItem value="curator">Curator</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={createInvite} disabled={creating} data-testid="create-invite-btn" className="bg-primary hover:bg-primary/90">
            <UserPlus className="w-4 h-4 mr-2" /> {creating ? "Sending\u2026" : "Create invite"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          The email is pre-authorized. When they sign in with Google, their role is applied automatically. Share the copyable link below.
        </p>

        {invites.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="invites-list">
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 flex-wrap bg-secondary/40 border border-border rounded-md px-4 py-2.5" data-testid={`invite-${inv.id}`}>
                <span className="font-medium text-foreground text-sm">{inv.email}</span>
                <RoleBadge role={inv.role} />
                {inv.accepted
                  ? <Badge variant="outline" className="text-accent border-accent/40">Accepted</Badge>
                  : <Badge variant="outline" className="text-muted-foreground">Pending</Badge>}
                <div className="ml-auto flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => copyLink(inv)} data-testid={`copy-invite-${inv.id}`}>
                    {copied === inv.id ? <Check className="w-4 h-4 mr-1 text-accent" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copy link
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => revokeInvite(inv.id)} data-testid={`revoke-invite-${inv.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Users */}
      <div className="bg-card border border-border rounded-lg shadow-sm mt-6 overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
          <Users className="w-5 h-5 text-primary" />
          <h2 className="font-serif text-2xl text-foreground">Members ({users.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-secondary/60 text-left text-secondary-foreground">
                <th className="px-6 py-3 font-semibold">Member</th>
                <th className="px-6 py-3 font-semibold hidden md:table-cell">Email</th>
                <th className="px-6 py-3 font-semibold">Current role</th>
                <th className="px-6 py-3 font-semibold w-44">Set role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isPrimaryAdmin = u.email.toLowerCase() === "shobhna.s@gmail.com";
                return (
                  <tr key={u.user_id} className="border-t border-border hover:bg-secondary/30 transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture
                          ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                          : <span className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-semibold">{u.name?.[0]?.toUpperCase()}</span>}
                        <div>
                          <div className="font-medium text-foreground">{u.name}</div>
                          {isPrimaryAdmin && <div className="text-[11px] text-primary font-semibold">Primary admin</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 hidden md:table-cell text-muted-foreground">{u.email}</td>
                    <td className="px-6 py-3"><RoleBadge role={u.role} /></td>
                    <td className="px-6 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isPrimaryAdmin}>
                        <SelectTrigger data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="learner">Learner</SelectItem>
                          <SelectItem value="curator">Curator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Maintenance */}
      <div className="bg-card border border-border rounded-lg shadow-sm p-6 mt-6" data-testid="maintenance-panel">
        <div className="flex items-center gap-2 mb-1">
          <Wrench className="w-5 h-5 text-primary" />
          <h2 className="font-serif text-2xl text-foreground">Maintenance</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Convert legacy UUID entity IDs (e.g. <code>concept_ab12cd34ef</code>) to readable slugs (<code>con_karma</code>).
          Safe and idempotent — it also updates every relationship, note and link that points to them. Preview first.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={previewMigration} disabled={migBusy} data-testid="migrate-preview-btn">
            {migBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
            Check for legacy IDs
          </Button>
          {migPlan && migPlan.found > 0 && !migPlan.applied && (
            <Button onClick={() => setMigConfirm(true)} disabled={migBusy} className="bg-primary hover:bg-primary/90" data-testid="migrate-apply-btn">
              Convert {migPlan.found} ID{migPlan.found === 1 ? "" : "s"} to slugs
            </Button>
          )}
        </div>

        {migPlan && (
          <div className="mt-4 text-sm" data-testid="migrate-result">
            {migPlan.found === 0 ? (
              <p className="text-accent">✓ All entity IDs already use slugs — nothing to migrate.</p>
            ) : (
              <>
                <p className="text-muted-foreground mb-2">
                  {migPlan.applied ? "Converted:" : "Will convert:"} {migPlan.found} ID{migPlan.found === 1 ? "" : "s"}
                  {migPlan.applied && <span className="text-accent font-semibold"> — done</span>}
                </p>
                <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border">
                  {migPlan.changes.map((c) => (
                    <div key={c.old} className="flex items-center gap-2 px-3 py-1.5 font-mono text-xs" data-testid={`migrate-change-${c.old}`}>
                      <span className="text-muted-foreground line-through">{c.old}</span>
                      <span className="text-primary">→</span>
                      <span className="text-foreground font-semibold">{c.new}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <AlertDialog open={migConfirm} onOpenChange={setMigConfirm}>
          <AlertDialogContent data-testid="migrate-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Convert {migPlan?.found} entity ID{migPlan?.found === 1 ? "" : "s"}?</AlertDialogTitle>
              <AlertDialogDescription>
                This rewrites the IDs and every relationship, note and link pointing to them. It runs immediately and cannot be undone (though it is safe and idempotent). Existing shared links using old IDs will stop resolving.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="migrate-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={applyMigration} data-testid="migrate-confirm">Convert now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminView;
